import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';

interface LoadResult<T> {
  key: unknown;
  data: T | null;
  error: string | null;
}

const message = (err: unknown) => (err instanceof Error ? err.message : 'Failed to load');

/**
 * Loads data on mount and whenever `key` changes (e.g. the active organization id), with
 * loading/error state, a reload function and a local setter for optimistic updates.
 */
export function useLoader<T>(load: () => Promise<T>, key: unknown = null) {
  const [result, setResult] = useState<LoadResult<T> | null>(null);
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    let cancelled = false;
    loadRef.current().then(
      (data) => !cancelled && setResult({ key, data, error: null }),
      (err: unknown) => !cancelled && setResult({ key, data: null, error: message(err) }),
    );
    return () => {
      cancelled = true;
    };
  }, [key]);

  const reload = useCallback(async () => {
    try {
      const data = await loadRef.current();
      setResult({ key, data, error: null });
    } catch (err) {
      setResult((r) => ({ key, data: r?.data ?? null, error: message(err) }));
    }
  }, [key]);

  const setData = useCallback((data: T) => setResult({ key, data, error: null }), [key]);

  const current = result?.key === key ? result : null;
  return {
    data: current?.data ?? null,
    error: current?.error ?? null,
    loading: current === null,
    reload,
    setData,
  };
}

/**
 * Form submission state: pending flag, a form-level error and per-field errors mapped from
 * the API's validation response.
 */
export function useSubmit() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const run = useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    setPending(true);
    setError(null);
    setFieldErrors({});
    try {
      return await fn();
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) {
        setFieldErrors(err.fields);
        if (err.code !== 'bad_request') setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong');
      }
      return undefined;
    } finally {
      setPending(false);
    }
  }, []);

  return { pending, error, fieldErrors, run, setError, setFieldErrors };
}

/** Reads a token from the URL once, then removes it so it is not kept in history. */
export function useUrlToken(): string | null {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'));
  useEffect(() => {
    if (token) window.history.replaceState(null, '', window.location.pathname);
  }, [token]);
  return token;
}

/** Keyset-paginated list: first page on mount / key change, then loadMore(). */
export function usePaged<T>(
  fetchPage: (cursor: string | null) => Promise<{ items: T[]; nextCursor: string | null }>,
  key: unknown,
) {
  const first = useLoader(() => fetchPage(null), key);
  const [extra, setExtra] = useState<{ key: unknown; items: T[]; cursor: string | null } | null>(
    null,
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  const current = extra?.key === key ? extra : null;
  const items = first.data ? [...first.data.items, ...(current?.items ?? [])] : [];
  const cursor = current ? current.cursor : (first.data?.nextCursor ?? null);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await fetchPage(cursor);
      setExtra({ key, items: [...(current?.items ?? []), ...page.items], cursor: page.nextCursor });
    } catch (err) {
      setMoreError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoadingMore(false);
    }
  };

  const reload = async () => {
    setExtra(null);
    await first.reload();
  };

  return {
    items,
    loading: first.loading,
    error: first.error ?? moreError,
    hasMore: cursor !== null,
    loadMore,
    loadingMore,
    reload,
  };
}
